import moment from 'moment';
import { ThreadObject, ThreadStatus } from '@varos/interview-sdk';
import { PageIds } from '../../../../../config';
import { AssistantControllerBaseConfig } from '../../../base';
import { ThreadOverviewController } from './threadOverviewInterface';
import {
    ThreadOverviewControllerProps,
    ThreadOverviewViewProps,
} from './threadOverviewProps';
import { isEqual } from 'lodash';
import { NavigateProps } from 'react-router';
import {
    AiOutlineMessage,
    AiOutlineCheckCircle,
    AiOutlineInfoCircle,
    AiOutlineInbox,
} from 'react-icons/ai';
import { ThreadItemProps } from '../item';
import { assertNever } from '@varos/util-typescript';

export function createThreadOverviewController(
    config: AssistantControllerBaseConfig
): ThreadOverviewController {
    const { navigation } = config;
    const {
        kernel: {
            infra: { formatter },
            adapter: { help },
        },
    } = config;

    function buildStatusProps(status: ThreadStatus): ThreadItemProps['status'] {
        if (status === 'open') {
            return {
                colorScheme: 'orange',
                label: 'In progress',
                tooltip: {
                    label: 'This interview is currently in progress',
                },
            };
        }
        if (status === 'closed') {
            return {
                colorScheme: 'gray',
                label: 'Completed',
                tooltip: {
                    label: 'This interview has been completed',
                },
            };
        }
        assertNever(status);
    }

    function buildItemProps(thread: ThreadObject): ThreadItemProps {
        return {
            id: thread.id,
            label: thread.title ?? 'Untitled',
            Icon: AiOutlineMessage,
            status: buildStatusProps(thread.status),
            timestamp: formatter.timeago(thread.created_at),
            link: navigation.thread.detail({
                thread: thread,
                mode: null,
                token: null,
                returnPath: null,
            }),
        };
    }

    return {
        useProps(context, item, props): ThreadOverviewViewProps {
            let empty: ThreadOverviewViewProps['empty'] = null;
            if (item.pending.length === 0) {
                if (item.completed.length > 0) {
                    empty = {
                        title: "You're all caught up!",
                        description:
                            'No pending interviews at the moment. Check back later for new opportunities.',
                        Icon: AiOutlineCheckCircle,
                        colorScheme: 'green',
                    };
                } else {
                    empty = {
                        title: 'No interviews available',
                        description: 'Check back later for new interview opportunities',
                        Icon: AiOutlineInbox,
                        colorScheme: 'gray',
                    };
                }
            }
            return {
                hero: {
                    action: {
                        learn: {
                            label: 'Questions? Ask us',
                            button: {
                                onClick() {
                                    help.open();
                                },
                            },
                        },
                    },
                },
                thread: {
                    pending: item.pending.map((thread) => buildItemProps(thread)),
                    completed: item.completed.map((thread) => buildItemProps(thread)),
                },
                empty,
            };
        },
    };
}
